import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ordersApi } from '@/api/orders'
import { billingApi } from '@/api/billing'
import type { OrderListFilters, RefundOrderPaymentInput, UpdateOrderStatusInput } from '@/types/ecommerce'

/**
 * Order data hooks — local to admin-dashboard (not shared-core) because shared-core only
 * ships the raw `ordersApi`/`billingApi` clients for this module, not React Query wrappers
 * yet (unlike bookingApi, which already has useBookings.ts in shared-core). Mirrors that
 * file's shape/conventions so it's a drop-in move if these are ever promoted to shared-core.
 *
 * `useOrdersList` returns every order (staff-wide) for a manager/admin caller, optionally
 * scoped via `filters.customerId`; a non-staff caller always gets only their own orders,
 * enforced server-side regardless of any customerId they might pass.
 */

const keys = {
  list: (filters: OrderListFilters) => ['orders', 'list', filters] as const,
  detail: (id: string) => ['orders', 'detail', id] as const,
  history: (id: string, cursor?: string) => ['orders', 'detail', id, 'history', cursor ?? 'first'] as const,
}

export function useOrdersList(filters: OrderListFilters = {}) {
  return useQuery({
    queryKey: keys.list(filters),
    queryFn: () => ordersApi.list(filters),
  })
}

export function useOrder(id: string | undefined) {
  return useQuery({
    queryKey: keys.detail(id ?? ''),
    queryFn: () => ordersApi.get(id!),
    enabled: !!id,
  })
}

export function useOrderHistory(id: string | undefined, cursor?: string) {
  return useQuery({
    queryKey: keys.history(id ?? '', cursor),
    queryFn: () => ordersApi.getHistory(id!, cursor),
    enabled: !!id,
  })
}

/** manager+ — the general status-change entry point (Pending→Processing→Completed, or
 * →Cancelled/→Refunded per OrderDomain.TransitionTo's matrix). Requires the rowVersion last
 * read via useOrder() — a stale value 409s instead of clobbering an interim change. */
export function useUpdateOrderStatus(orderId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateOrderStatusInput) => ordersApi.updateStatus(orderId!, body),
    onSuccess: () => {
      if (orderId) {
        qc.invalidateQueries({ queryKey: keys.detail(orderId) })
        qc.invalidateQueries({ queryKey: ['orders', 'detail', orderId, 'history'] })
      }
    },
  })
}

/** manager+ — refunds the Stripe payment via BillingService (order-scoped, not
 * paymentId-scoped). Independent of the OrderService status machine: this moves money back
 * to the customer but does not itself flip Order.Status — pair it with a status update to
 * 'Refunded' (legal only from 'Completed') if the order's own record should reflect that. */
export function useRefundOrderPayment(orderId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: RefundOrderPaymentInput) => billingApi.refundOrderPayment(body),
    onSuccess: () => {
      if (orderId) qc.invalidateQueries({ queryKey: keys.detail(orderId) })
    },
  })
}
